import { z } from 'zod';
import { isoDateSchema } from './common.js';

/** A file or directory as the client API returns it. */
export const fileEntrySchema = z.object({
  name: z.string(),
  size: z.number(),
  /** e.g. "-rw-r--r--" */
  mode: z.string(),
  /** e.g. "644" */
  modeBits: z.string(),
  isFile: z.boolean(),
  isDirectory: z.boolean(),
  isSymlink: z.boolean(),
  mimetype: z.string(),
  /** Protected by the egg's denylist: can't be opened, changed or deleted. */
  denied: z.boolean(),
  modifiedAt: isoDateSchema,
});
export type FileEntry = z.infer<typeof fileEntrySchema>;

/** hearthd's file entry (snake_case). */
export const daemonFileEntrySchema = z
  .object({
    name: z.string(),
    size: z.number(),
    mode: z.string(),
    mode_bits: z.string(),
    is_file: z.boolean(),
    is_directory: z.boolean(),
    is_symlink: z.boolean(),
    mimetype: z.string(),
    denied: z.boolean(),
    modified_at: z.string(),
  })
  .transform((e): FileEntry => ({
    name: e.name,
    size: e.size,
    mode: e.mode,
    modeBits: e.mode_bits,
    isFile: e.is_file,
    isDirectory: e.is_directory,
    isSymlink: e.is_symlink,
    mimetype: e.mimetype,
    denied: e.denied,
    modifiedAt: e.modified_at,
  }));

const pathSchema = z
  .string()
  .max(4096)
  .refine((p) => !p.includes('\0'), 'Paths cannot contain NUL bytes.');
const nameSchema = pathSchema.refine((p) => p.trim() !== '', 'Enter a name.');

export const fileListQuerySchema = z.object({ directory: pathSchema.default('/') });
export const filePathQuerySchema = z.object({ file: nameSchema });
export const fileWriteQuerySchema = z.object({
  file: nameSchema,
  /** create: fail if it exists; update: fail if it doesn't. */
  mode: z.enum(['create', 'update']).default('update'),
});

export const renameFilesSchema = z.object({
  root: pathSchema.default('/'),
  files: z
    .array(z.object({ from: nameSchema, to: nameSchema }))
    .min(1)
    .max(500),
});
export const copyFileSchema = z.object({ location: nameSchema });
export const deleteFilesSchema = z.object({
  root: pathSchema.default('/'),
  files: z.array(nameSchema).min(1).max(500),
});
export const createFolderSchema = z.object({ root: pathSchema.default('/'), name: nameSchema });
export const chmodFilesSchema = z.object({
  root: pathSchema.default('/'),
  files: z
    .array(
      z.object({
        file: nameSchema,
        mode: z.string().regex(/^[0-7]{3}$/, 'Use three octal digits, like 644.'),
      }),
    )
    .min(1)
    .max(500),
});
export const compressFilesSchema = z.object({
  root: pathSchema.default('/'),
  files: z.array(nameSchema).min(1).max(500),
  format: z.enum(['tar.gz', 'zip']).default('tar.gz'),
});
export const decompressFileSchema = z.object({ root: pathSchema.default('/'), file: nameSchema });

/** A signed URL on the node for a direct browser download or upload. */
export const signedUrlSchema = z.object({ url: z.string(), expiresAt: isoDateSchema });

/** Upload link plus the largest chunk the node accepts per request. */
export const uploadUrlSchema = signedUrlSchema.extend({ chunkBytes: z.number().int() });

/** How to connect over SFTP. */
export const sftpDetailsSchema = z.object({
  host: z.string(),
  port: z.number().int(),
  username: z.string(),
});

// ---------- SSH keys ----------

export const sshKeySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  fingerprint: z.string(),
  publicKey: z.string(),
  lastUsedAt: isoDateSchema.nullable(),
  createdAt: isoDateSchema,
});
export type SshKey = z.infer<typeof sshKeySchema>;

export const createSshKeySchema = z.object({
  name: z.string().trim().min(1).max(191),
  publicKey: z.string().trim().min(1).max(16_384),
});
